import { ref } from 'vue'

import { useInstanceCapabilitiesStore } from 'src/stores/instance_capabilities.js'
import { useInterfaceStore } from 'src/stores/interface.js'
import { useMergedConfigStore } from 'src/stores/merged_config.js'
import { useNotificationsStore } from 'src/stores/notifications.js'

import { fetchTimeline } from 'src/api/timelines.js'
import { catchUp } from 'src/services/catch_up/catch_up.js'
import { compareIds, idBefore } from 'src/services/ids/ids.js'
import { promiseInterval } from 'src/services/promise_interval/promise_interval.js'

// every how many polls the unread notifications are checked again
const RESYNC_EVERY = 6

// For using include_types when fetching notifications.
// Note: chat_mention excluded as pleroma-fe polls them separately
const mastoApiNotificationTypes = new Set([
  'mention',
  'status',
  'favourite',
  'reblog',
  'follow',
  'follow_request',
  'move',
  'poll',
  'pleroma:emoji_reaction',
  'pleroma:report',
])

const notificationsFetcher = (credentials) => {
  const interval = ref(null)
  const loading = ref(false)
  // only a fetch of older ones: the "load older" button follows this, not
  // the poll for new ones every few seconds
  const loadingOlder = ref(false)
  const bottomedOut = ref(false)
  let polls = 0

  const fetchNotifications = async ({ args, older }) => {
    loading.value = true
    if (older) loadingOlder.value = true
    const clears = useNotificationsStore().clears

    try {
      const response = await fetchTimeline(args)
      // cleared while this ran: the answer may hold what was cleared
      if (useNotificationsStore().clears !== clears) return
      const notifications = response.data
      if (older && notifications.length === 0) bottomedOut.value = true

      useNotificationsStore().addNewNotifications(response, older)
    } catch (error) {
      // the server refuses some notification types: retry without them,
      // only if one could be dropped (the same list is refused forever)
      const refused =
        error.statusCode === 400 && typeof error.statusText === 'string'
          ? [
              ...error.statusText.matchAll(/(\w+) - Invalid value for enum./g),
            ].map(([, type]) => type)
          : []
      const dropped = refused.filter((type) =>
        mastoApiNotificationTypes.delete(type),
      )
      if (dropped.length > 0) {
        return await fetchNotifications({
          args: { ...args, includeTypes: [...mastoApiNotificationTypes] },
          older,
        })
      }

      console.error('Notifications Error', error)
      useInterfaceStore().pushGlobalNotice({
        level: 'error',
        messageKey: 'notifications.error',
        messageArgs: [error.message],
        timeout: 5000,
        error,
      })
    } finally {
      loading.value = false
      if (older) loadingOlder.value = false
    }
  }

  const fetchAndUpdate = async ({ older = false, sinceId } = {}) => {
    const args = { credentials }
    const timelineData = useNotificationsStore()
    const hideMutedPosts = useMergedConfigStore().mergedConfig.hideMutedPosts

    if (useInstanceCapabilitiesStore().pleromaChatMessagesAvailable) {
      mastoApiNotificationTypes.add('pleroma:chat_mention')
    }

    args.includeTypes = [...mastoApiNotificationTypes]
    args.withMuted = !hideMutedPosts

    args.timeline = 'notifications'
    if (older) {
      if (timelineData.minId !== '') {
        args.maxId = timelineData.minId
      }
      return await fetchNotifications({ args, older })
    } else {
      // fetch new notifications
      if (sinceId === undefined && timelineData.maxId !== '') {
        args.sinceId = timelineData.maxId
      } else if (sinceId !== null) {
        args.sinceId = sinceId
      }
      const result = await fetchNotifications({ args, older })

      // If there's any unread notifications, try fetch notifications since
      // the newest read notification to check if any of the unread notifs
      // have changed their 'seen' state (marked as read in another session), so
      // we can update the state in this session to mark them as read as well.
      // The normal maxId-check does not tell if older notifications have changed
      if (polls++ % RESYNC_EVERY === 0) {
        const notifications = timelineData.data
        const unread = notifications.filter((n) => !n.seen).map((n) => n.id)
        const oldestUnread = unread.length
          ? unread.reduce((a, b) => (compareIds(a, b) <= 0 ? a : b))
          : null
        const minId = oldestUnread && idBefore(oldestUnread)
        if (minId && notifications.some((n) => n.seen)) {
          // no since_id: it conflicts with min_id
          fetchNotifications({ args: { ...args, sinceId: null, minId }, older })
        }
      }

      return result
    }
  }

  // the newest ones: the poll and the stream's catch-up
  const newer = catchUp(fetchAndUpdate)

  const startFetching = () => {
    if (interval.value) throw new Error('Interval already exists!')
    // after a pause (e.g. the stream dropped) check the unread ones at once
    polls = 0

    interval.value = promiseInterval(() => newer.run(), 10000)
  }

  // also when already stopped: a logout pauses, then deactivates
  const stopFetching = () => {
    interval.value?.stop()
    interval.value = null
    newer.stop()
  }

  return {
    loading,
    loadingOlder,
    bottomedOut,
    startFetching,
    stopFetching,
    fetchOlder: () => fetchAndUpdate({ older: true }),
    // one poll, as the interval runs it; once after a running one
    fetchNewer: newer.runNewer,
  }
}

export default notificationsFetcher
